import { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { M3Cull } from './api';
import { ApiProvider } from './ui/api-context';
import Home from './ui/routes/Home';
import Project from './ui/routes/Project';
import Review from './ui/routes/Review';
import Shoots from './ui/routes/Shoots';
import Unsupported from './ui/routes/Unsupported';
import NotFound from './ui/routes/NotFound';
import type { BlockReason } from './api';

export type BootState =
  | { phase: 'ready'; api: M3Cull; blockedBy: BlockReason | null }
  | { phase: 'blocked' }
  | { phase: 'failed'; error: string };

/**
 * Boots the API before React mounts.
 *
 * index.html ships with a prerendered copy of the landing page (see
 * scripts/prerender.mjs) so crawlers that do not run JavaScript still read the
 * product, and visitors see content instantly. Booting first means that copy
 * stays on screen until the live page is ready to replace it, rather than
 * flashing to a spinner in between.
 */
export async function bootApp(): Promise<BootState> {
  try {
    const api = await M3Cull.boot();
    const [access, capabilities] = await Promise.all([api.access(), M3Cull.capabilities()]);
    return access.allowed
      ? { phase: 'ready', api, blockedBy: capabilities.blockedBy }
      : { phase: 'blocked' };
  } catch (err) {
    return { phase: 'failed', error: err instanceof Error ? err.message : String(err) };
  }
}

export default function App({ state }: { state: BootState }) {
  if (state.phase === 'failed') {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <div className="card max-w-md p-6">
          <h1 className="text-head font-medium">m3cull could not start</h1>
          <p className="mt-2 text-caption text-ink-400">{state.error}</p>
          <button className="btn-outline mt-5" onClick={() => location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (state.phase === 'blocked') {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <div className="card max-w-md p-6">
          <h1 className="text-head font-medium">m3cull needs a licence</h1>
          <p className="mt-2 text-caption text-ink-400">
            This copy is configured to require a licence key.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ApiProvider value={state.api}>
      {/* First tab stop on every screen, visible only once it has focus. */}
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <Resume api={state.api} />
      <Routes>
        <Route
          path="/"
          element={state.blockedBy ? <Unsupported reason={state.blockedBy} /> : <Home />}
        />
        <Route path="/shoots" element={<Shoots />} />
        <Route path="/p/:projectId" element={<Project />} />
        <Route path="/p/:projectId/review" element={<Review />} />
        {/* A wrong address gets an answer, not a silent bounce to the home page. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ApiProvider>
  );
}

const LAST_ROUTE = 'last-route';
const RESUMED = 'm3cull.resumed';

/**
 * Puts you back where you left off — once per session.
 *
 * Opening m3cull to a marketing page you have already read is friction, so a
 * returning visitor is sent back to their last screen. Only on the first load of
 * a session, though: after that the wordmark has to actually reach the home
 * page rather than bouncing you away from it.
 */
function Resume({ api }: { api: M3Cull }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname !== '/') return;
    try {
      if (sessionStorage.getItem(RESUMED)) return;
      sessionStorage.setItem(RESUMED, '1');
    } catch {
      return; // storage blocked; just show the home page
    }

    const last = api.getSetting(LAST_ROUTE);
    if (last && last !== '/' && api.listProjects().length > 0) navigate(last, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remember every screen except the landing page itself.
  useEffect(() => {
    if (location.pathname !== '/') api.setSetting(LAST_ROUTE, location.pathname);
  }, [api, location.pathname]);

  return null;
}
